import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays, Calendar, Tag, Users, Waves, Sparkles } from "lucide-react";
import heroImage from "@/assets/gallery/aerial-cabanas.jpg";
import cabanaImg from "@/assets/cabanas/cabana-riverside.jpg";
import tubingImg from "@/assets/tubing/family-tubing-fun.jpg";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";

const PUBLISH_DATE = "May 27, 2026";
const CANONICAL = "/blog/labor-day-weekend-guadalupe-river";
const TITLE = "Labor Day on the Guadalupe — 2026 Tubing Guide";
const DESCRIPTION =
  "Plan Labor Day weekend on the Guadalupe near New Braunfels — what's open at Son's, booking deadlines, end-of-season pricing, and how to skip crowds.";

const FAQS = [
  {
    q: "Is Son's Guadalupe open Labor Day weekend?",
    a: "Yes — Son's Guadalupe is fully open Saturday Sept 5 through Monday Sept 7, 2026 for tubing, cabana rentals, glamping, and overnight camping. Labor Day is the official last big weekend of the Guadalupe tubing season.",
  },
  {
    q: "When should I book Labor Day weekend?",
    a: "Labor Day is the second-busiest weekend after July 4th. Book cabanas, glamping, and overnight stays 4–6 weeks in advance. Day cabanas on Saturday Sept 5 and Sunday Sept 6 typically sell out by mid-August.",
  },
  {
    q: "What's the weather like on the Guadalupe in early September?",
    a: "Early September on the Guadalupe is hot and dry — daytime highs in the low-to-mid 90s and water temps still in the high 70s. The river feels amazing. Mornings and evenings cool down nicely for fire pits and grilling.",
  },
  {
    q: "Is tubing still good on Labor Day weekend?",
    a: "Yes — late-summer river flow is steady and predictable. By Labor Day the crowds at public access points are easing off, but private outfitters like Son's still book full. The 2–3 hour float is the same all summer.",
  },
  {
    q: "How do I avoid Labor Day crowds on the river?",
    a: "Book a private cabana so you skip the parking-lot scramble, arrive at 10:30 AM open, and grill lunch on-site instead of leaving for food. Overnight glamping and cabin guests have the river practically to themselves before 10:30 AM and after 6:30 PM.",
  },
  {
    q: "Is this the end of the tubing season?",
    a: "Labor Day is the unofficial last big weekend. Son's continues weekend operations through September and into October depending on weather and river flow — but Labor Day is the last weekend you can count on full operations, full sun, and warm water.",
  },
];

const schemaJson = [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "Labor Day Weekend on the Guadalupe River — 2026 Cabanas & Tubing Guide",
    description: DESCRIPTION,
    image: "https://sonsguadalupe.com/og-image.png",
    author: { "@type": "Organization", name: "Son's Guadalupe" },
    publisher: {
      "@type": "Organization",
      name: "Son's Guadalupe",
      logo: { "@type": "ImageObject", url: "https://sonsguadalupe.com/og-image.png" },
    },
    datePublished: "2026-05-27",
    dateModified: "2026-05-27",
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
    keywords:
      "labor day guadalupe river, labor day new braunfels, labor day tubing, end of summer cabana rental, september texas river",
  },
  {
    "@context": "https://schema.org",
    "@type": "Event",
    name: "Labor Day Weekend on the Guadalupe River at Son's",
    description:
      "Celebrate Labor Day weekend on the Guadalupe River at Son's Guadalupe — private cabanas, family tubing, glamping, and riverside grilling near New Braunfels, TX.",
    startDate: "2026-09-05T10:30:00-05:00",
    endDate: "2026-09-07T18:30:00-05:00",
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Son's Guadalupe",
      address: {
        "@type": "PostalAddress",
        streetAddress: "7308 River Road",
        addressLocality: "New Braunfels",
        addressRegion: "TX",
        postalCode: "78132",
        addressCountry: "US",
      },
    },
    image: "https://sonsguadalupe.com/og-image.png",
    organizer: {
      "@type": "Organization",
      name: "Son's Guadalupe",
      url: "https://sonsguadalupe.com",
    },
    offers: {
      "@type": "AggregateOffer",
      lowPrice: 69,
      highPrice: 899,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: "https://sonsguadalupe.com/book-now",
      validFrom: "2026-05-27",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
      { "@type": "ListItem", position: 3, name: "Labor Day on the Guadalupe River", item: `https://sonsguadalupe.com${CANONICAL}` },
    ],
  },
];

const BlogLaborDay2026 = () => {
  const related = getRelatedPosts("labor-day-weekend-guadalupe-river", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} ogImage="/og/blog-memorial-day.jpg" schemaJson={schemaJson} />
      <Header />
      <BlogBreadcrumb />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Labor Day weekend cabanas on the Guadalupe River near New Braunfels" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Labor Day Weekend on the Guadalupe River — 2026 Cabanas & Tubing Guide
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                <strong>Labor Day weekend on the Guadalupe River</strong> is the last big hurrah of the Texas summer — warm spring-fed water, riverside grilling, kids tubing all afternoon, and that "one more weekend before school" feel. Here's how to plan a smooth, sold-out-proof Labor Day at Son's.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Calendar className="w-5 h-5 text-teal" /> Labor Day 2026 Dates</h2>
              <p>
                Labor Day 2026 falls on <strong>Monday, September 7</strong>, making it a natural 3-day weekend (Sat Sept 5 – Mon Sept 7). Early September on the Guadalupe is still peak season — daytime highs in the low-to-mid 90s and water temps in the high 70s. The river is at its clearest of the year.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Waves className="w-5 h-5 text-teal" /> What's Open at Son's on Labor Day</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Cabana rentals</strong> — all 22 private riverside cabanas open 10:30 AM – 6:30 PM</li>
                <li><strong>In-water picnic tables</strong> — riverside tables right in the shallow water</li>
                <li><strong>Tubing</strong> — heavy-duty Son's tubes, $29.99 per person ($24.99 with cabana)</li>
                <li><strong>Glamping</strong> — AC-cooled bell tents with queen beds and private fire pits</li>
                <li><strong>Tent camping & overnight stays</strong> — riverside sites at Son's River Ranch, Blue River Camp, and Rio Cibolo</li>
              </ul>

              <img src={cabanaImg} alt="Labor Day cabana setup on the Guadalupe River near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Tag className="w-5 h-5 text-teal" /> Pricing & Booking Deadlines</h2>
              <p>
                Cabana rentals start at <strong>$69</strong> and run higher on the holiday weekend due to demand. Glamping starts at <strong>$129/night</strong> and overnight cabins from <strong>$199/night</strong>. Labor Day weekend usually sells out 3–5 weeks in advance — if you haven't booked by mid-August, don't wait.
              </p>
              <p>
                A 50% deposit holds your reservation; the remaining balance is due 7 days before arrival. Cancellation: more than 15 days out gets a 90% refund, 7–14 days gets 50%, inside 7 days is non-refundable.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Sparkles className="w-5 h-5 text-teal" /> End-of-Summer Weather</h2>
              <p>
                Early September in the Texas Hill Country is reliably hot and dry. Expect daytime highs around 92–96°F, water temperatures in the high 70s, and clear skies most of the weekend. Afternoon thunderstorms are possible but usually short-lived. The river runs spring-fed and consistent — no flow surprises.
              </p>
              <p>
                Mornings and evenings cool down to the low 70s — perfect for fire pits, grilling, and stargazing from your glamping tent.
              </p>

              <img src={tubingImg} alt="Family tubing the Guadalupe River on Labor Day weekend" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Users className="w-5 h-5 text-teal" /> Kid-Friendly Labor Day Tips</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Book a cabana — your private shaded basecamp makes the last big weekend infinitely easier with kids</li>
                <li>Arrive at 10:30 AM open. Mid-day parking is the worst of the holiday crunch.</li>
                <li>Bring kids' life jackets (we have rentals if you forget). Required for non-swimmers.</li>
                <li>Pack a back-to-school camera moment — Labor Day photos on the river make great first-day-of-school throwbacks</li>
                <li>Grill burgers and dogs at the cabana — way faster than fighting Labor Day restaurant lines in town</li>
                <li>Reserve a glamping tent if you want to stretch the summer one more night</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">How to Beat the Labor Day Crowds</h2>
              <p>
                The single biggest hack: <strong>book a private cabana or stay overnight.</strong> Day-trippers without reservations spend Labor Day hunting for parking and standing in line at public outfitters. Son's guests check in, walk to their cabana, and have river access all day. Overnight guests have the Guadalupe practically to themselves before 10:30 AM and after 6:30 PM — the best floats of the entire holiday.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Labor Day Packing Checklist</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Reef-safe sunscreen (high SPF — Texas sun is still brutal in September)</li>
                <li>Coolers with ice (no glass on the river)</li>
                <li>Life jackets for non-swimmers and kids</li>
                <li>Water shoes — the riverbed has rocks</li>
                <li>Burgers, dogs, charcoal — your cabana has a grill</li>
                <li>Bug spray for the evening</li>
                <li>Light layer for cooler evenings around the fire pit</li>
                <li>Phone in a waterproof pouch for cabana photos</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Labor Day FAQ</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book Labor Day Weekend</Button></Link>
                <Link to="/cabanas"><Button variant="outline" size="lg" className="text-lg px-10 py-6">See Cabanas</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />
      </main>
      <SEOContentBlock
        eyebrow="Plan Your Labor Day"
        heading="Cabanas, Tubing & Glamping for Labor Day on the Guadalupe"
        paragraphs={[
          "Labor Day weekend on the Guadalupe River is the official last hurrah of the Texas summer — and the second-busiest weekend of the season. Lock in a cabana rental on the Guadalupe River, glamping near New Braunfels, or overnight camping on River Road before the calendar fills.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "Book Now", to: "/book-now" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogLaborDay2026;
